import { Controller } from '@hotwired/stimulus';

/**
 * Menu déroulant du bouton compte (header onboarding / espace client).
 *
 * Ouvre et ferme le menu au clic sur le bouton, et le referme sur un clic
 * en dehors ou sur la touche Échap (le focus revient alors sur le bouton).
 * `aria-expanded` est synchronisé à chaque changement d'état.
 */
export default class extends Controller<HTMLElement> {
  static targets = ['button', 'menu'];

  declare readonly buttonTarget: HTMLButtonElement;
  declare readonly menuTarget: HTMLElement;

  private onDocumentClick = (event: MouseEvent): void => {
    if (!this.element.contains(event.target as Node)) {
      this.close();
    }
  };

  private onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      this.close();
      this.buttonTarget.focus();
    }
  };

  connect (): void {
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
  }

  disconnect (): void {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
  }

  toggle (event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.menuTarget.classList.contains('hidden')) {
      this.open();
    } else {
      this.close();
    }
  }

  private open (): void {
    this.menuTarget.classList.remove('hidden');
    this.buttonTarget.setAttribute('aria-expanded', 'true');
  }

  private close (): void {
    if (this.menuTarget.classList.contains('hidden')) {
      return;
    }

    this.menuTarget.classList.add('hidden');
    this.buttonTarget.setAttribute('aria-expanded', 'false');
  }
}
